import IframeView from 'epubjs/src/managers/views/iframe';

/*
 Frog toolbar books share localStorage with the parent app (srcdoc iframes are same-origin).
 Two toolbar generations exist:

   v1 (lcd-libs.min.js / jQuery-based)
     — Never calls localStorage.clear().
     — Loads timer JS files via $.getScript(), which appends ?_=timestamp cache-busters.
       BunnyCDN signed-URL validation rejects URLs with extra query params → 403.
       Fix: strip ?_=timestamp from XHR open() before the request reaches the CDN.

   v2 (toolbar-core / React-based)
     — Calls localStorage.clear() on FROG_TOOLBAR_BUILD_TIMESTAMP mismatch.
       Because the iframe is same-origin (srcdoc), this wipes the parent app's storage too.
     — Authenticates CDN audio via URL-embedded tokens, so cookies are not required.

 Strategy:
   Chrome/Edge: iframe.credentialless=true isolates the iframe's storage partition from the
     parent at the browser level — localStorage.clear() in the iframe only affects its own
     isolated partition, never the parent's.
   Firefox/Safari (no credentialless support): override clear() via the Storage prototype in
     onLoad() as a no-op. Belt-and-suspenders: also inject a clear() no-op as the first
     script in <head> so it runs before any toolbar code (onLoad fires too late).

 Settings persistence across pages (both v1 and v2):
   With credentialless, each iframe has an isolated localStorage partition. To preserve user
   settings across page swipes, we maintain a parent-side backup (window._frogSettingsBackup)
   and inject it into each iframe's localStorage before toolbar code runs. Writes from the
   active iframe are mirrored back to the backup and trigger neighbour reloads.
*/

// Toolbar-internal keys — not user settings; excluded from backup and neighbour reload.
const SKIP_RELOAD_KEYS = new Set([
  'FROG_TOOLBAR_BUILD_TIMESTAMP',
  'FROG_TOOLBAR_VERSION',
  'frog_broadcast',
  'FROG_REACT_TOOLBAR_STATE',
  'FROG_KEEP_PLAYING_ON_PAGE_CHANGE',
  'onboarding',
]);

class FrogSafeIframeView extends IframeView {
  load(contents) {
    if (window.isFrogBook) {
      this._originalContents = contents;

      // Chrome/Edge: isolate the iframe's storage partition so the v2 toolbar's clear() cannot
      // reach the parent app's localStorage. Firefox/Safari don't support credentialless — they
      // rely on the prototype override in onLoad() and the <head> script injection below.
      this.iframe.credentialless = true;

      // Firefox/Safari: inject clear() no-op as the first script in <head> so it runs before
      // any toolbar code. Belt-and-suspenders for Chrome/Edge too (harmless with credentialless).
      const clearGuard =
        `<script>(function(){localStorage.clear=function(){` +
        `console.warn('[FrogSafeIframe] localStorage.clear() suppressed')` +
        `}})()</` +
        `script>`;

      // Restore user settings from the parent-side backup into this iframe's isolated
      // localStorage before any toolbar code runs, so the toolbar sees the correct state.
      const backup = window._frogSettingsBackup ?? {};
      const backupJson = JSON.stringify(backup);
      const settingsRestoreGuard =
        `<script>(function(){` +
        `var b=${backupJson};` +
        `Object.keys(b).forEach(function(k){try{localStorage.setItem(k,b[k])}catch(e){}})` +
        `})()</` +
        `script>`;

      // v1 toolbar loads timer JS via $.getScript(), which appends ?_=timestamp cache-busters.
      // BunnyCDN validates the signed URL path exactly — the extra query param breaks the
      // signature check and returns 403. Strip it from XHR open() before the request leaves.
      const cacheBusterGuard =
        `<script>(function(){` +
        `var oo=XMLHttpRequest.prototype.open;` +
        `XMLHttpRequest.prototype.open=function(m,u){` +
        `if(typeof u==='string'&&u.indexOf('/timers/')>=0)u=u.replace(/[?]_=[0-9]+$/,'');` +
        `var a=Array.prototype.slice.call(arguments);a[1]=u;return oo.apply(this,a)};` +
        `})()</` +
        `script>`;

      const injection = clearGuard + settingsRestoreGuard + cacheBusterGuard;
      const modified = contents.replace(/(<head(?:[^>]*)?>)/i, `$1${injection}`);
      // eslint-disable-next-line no-param-reassign
      contents = modified !== contents ? modified : injection + contents;
    }
    return super.load(contents);
  }

  // Called by the base IframeView via this.iframe.onload — dynamic dispatch, not dead code.
  onLoad(event, promise) {
    if (window.isFrogBook && this.iframe.contentWindow) {
      // Firefox/Safari: credentialless is not supported, so override clear() on the Storage
      // prototype as the primary protection against the v2 toolbar wiping parent storage.
      if (window.isFirefox || window.thisCouldBeSafari) {
        const proto = Object.getPrototypeOf(this.iframe.contentWindow.localStorage);
        proto.clear = function () {
          console.warn('Local storage clear function is overridden to prevent clearing the local storage from iframe');
        };
        Object.setPrototypeOf(this.iframe.contentWindow.localStorage, proto);
      }

      // Mirror user-setting writes from the active iframe to the parent-side backup and
      // signal SliderManager to reload neighbours so they pick up the updated values.
      const capturedStorage = this.iframe.contentWindow.localStorage;
      const nativeSetItem = Storage.prototype.setItem;
      capturedStorage.setItem = function (key, value) {
        nativeSetItem.call(capturedStorage, key, value);
        if (!SKIP_RELOAD_KEYS.has(key) && capturedStorage === window._frogActiveStorage) {
          if (!window._frogSettingsBackup) window._frogSettingsBackup = {};
          window._frogSettingsBackup[key] = value;
          window.dispatchEvent(new CustomEvent('frog-settings-changed'));
        }
      };
    }

    super.onLoad(event, promise);
  }
}

export default FrogSafeIframeView;
